import {
  Box,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  Divider,
  Th,
  Checkbox,
  Text,
  Tooltip,
  Button,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from '@chakra-ui/react'
import { ceil, isNull, isUndefined } from 'lodash'
import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDots } from 'react-icons/bs'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { GrTag } from 'react-icons/gr'
import { RiDeleteBinFill } from 'react-icons/ri'
import {
  CellProps,
  Column,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import { PermissionsEnum } from '../../../../utils'
import { DeleteFlagEnum, SortEnum, SortOperation } from '../../../constants'
import useToast from '../../../hooks/useToast'
import {
  ClientTagRequestResponse,
  ClientTagResponse,
} from '../../../interfaces'
import { UserContext } from '../../../providers'
import Pagination from '../../Pagination'
import PermissionGuard from '../../PermissionGuard'
import SearchBar from '../../SearchBar'
import DeleteDialog from './DeleteDialog'

const ClientTagsSettings = () => {
  const toast = useToast()
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [tagData, setTagData] = useState<ClientTagResponse | null>()
  const [selectedTags, setSelectedTags] = useState<ClientTagResponse[]>([])
  const [deleteFlag, setDeleteFlag] = useState<string>()
  const [tagsCount, setTagsCount] = useState<number>(0)
  const [searchText, setSearchText] = useState<string>('')
  const [sort, setSort] = useState<string>('')
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const { request, loading } = useFetch<ClientTagRequestResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [clientTags, setClientTags] = useState<ClientTagResponse[]>([])

  const {
    isOpen: isOpenDeleteTag,
    onOpen: onOpenDeleteUser,
    onClose: onCloseDeleteTag,
  } = useDisclosure()

  const handleTagsSelection = useCallback(
    data => {
      selectedTags.map(value => value.tagId).includes(data.tagId)
        ? setSelectedTags(prev =>
            prev.filter(value => value.tagId !== data.tagId),
          )
        : setSelectedTags(prev => [...prev, data])
    },
    [selectedTags, setSelectedTags],
  )

  const columns = useMemo<Array<Column<ClientTagResponse>>>(
    () => [
      {
        id: 'selected',
        width: '4.1em',
        disableSortBy: true,
        Cell: ({ row }: CellProps<ClientTagResponse>) => (
          <Checkbox
            isChecked={selectedTags
              .map(value => value.tagId)
              .includes(row.original.tagId)}
            onChange={() => handleTagsSelection(row.original)}
          />
        ),
      },
      {
        Header: t('tag', 'Tag').toString(),
        accessor: values => (
          <Tooltip label={values.label}>
            <Text isTruncated>{values.label}</Text>
          </Tooltip>
        ),
      },
      {
        Header: t('last_used', 'Last used').toString(),
        accessor: values => (
          <Tooltip
            label={moment(values.updatedAt).format('DD MMMM YYYY, hh:mm A')}
          >
            <Text cursor="pointer">{moment(values.updatedAt).fromNow()}</Text>
          </Tooltip>
        ),
      },
      {
        Header: t('created_on', 'Created on').toString(),
        accessor: values => (
          <Tooltip
            label={moment(values.createdAt).format('DD MMMM YYYY, hh:mm A')}
          >
            <Text cursor="pointer">{moment(values.createdAt).fromNow()}</Text>
          </Tooltip>
        ),
      },
      {
        Header: t('actions', 'Actions').toString(),
        disableSortBy: true,
        Cell: ({ value, row }: CellProps<ClientTagResponse, string>) => (
          <Flex justify="flex-start">
            <Box>{value}</Box>
            <PermissionGuard
              permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <IconButton
                    aria-label="Delete  Suggested Tag"
                    icon={<RiDeleteBinFill />}
                    colorScheme={colors.appBrick}
                    variant="link"
                    size="s"
                    onClick={() => {
                      setTagData({ ...row.original })
                      setDeleteFlag(DeleteFlagEnum.SINGLE_DELETE)
                      onOpenDeleteUser()
                    }}
                    disabled={selectedTags?.length > 0}
                  />
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
          </Flex>
        ),
      },
    ],
    [selectedTags, handleTagsSelection],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageSize },
    canNextPage,
    pageCount,
    setPageSize,
  } = useTable<ClientTagResponse>(
    { columns, data: clientTags },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const getTag = useCallback(async (): Promise<void> => {
    if (user?.clientNumber) {
      const params: Record<string, string> = {
        skip: `${currentPageIndex * pageSize}`,
        limit: pageSize.toString(),
        ...(searchText && { label: searchText }),
        ...(sort && { sort }),
      }
      const searchParams = new URLSearchParams(params)
      const url = `api/clients/${
        user.clientNumber
      }/client-tags?${searchParams.toString()}`
      const response = await request.get(url)
      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else if (
        response?.tagsData &&
        Array.isArray(response.tagsData) &&
        !(isUndefined(response.totalTags) || isNull(response.totalTags))
      ) {
        setClientTags(response.tagsData)
        setTagsCount(response.totalTags)
      }
    }
  }, [
    user,
    pageSize,
    currentPageIndex,
    searchText,
    sort,
    request,
    toast,
    setClientTags,
    setTagsCount,
  ])

  const deleteTag = useCallback(async (): Promise<void> => {
    if (
      user?.clientNumber &&
      ((DeleteFlagEnum.SINGLE_DELETE && tagData?.tagId) ||
        (DeleteFlagEnum.MULTIPLE_DELETE && selectedTags.length))
    ) {
      setIsDeleting(true)
      await request.post(
        `/api/clients/${user.clientNumber}/client-tags/bulk-delete`,
        {
          tagIds:
            deleteFlag === DeleteFlagEnum.SINGLE_DELETE
              ? [tagData?.tagId]
              : selectedTags.map(data => data.tagId),
        },
      )
      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        toast({
          description: t(
            'suggested_tags_removed_successfully',
            'The tag suggestions were successfully removed.',
          ),
          status: 'success',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        setCurrentPageIndex(0)
        getTag()
        setTagData(null)
        if (deleteFlag === DeleteFlagEnum.MULTIPLE_DELETE) {
          setSelectedTags([])
          setDeleteFlag('')
        } else if (
          deleteFlag === DeleteFlagEnum.SINGLE_DELETE &&
          selectedTags.length
        ) {
          setDeleteFlag(DeleteFlagEnum.MULTIPLE_DELETE)
        }
      }
      setIsDeleting(false)
    }
  }, [
    user,
    tagData,
    selectedTags,
    setIsDeleting,
    request,
    deleteFlag,
    toast,
    setCurrentPageIndex,
    getTag,
    setTagData,
    setSelectedTags,
    setDeleteFlag,
  ])

  useEffect(() => {
    void getTag()
  }, [user, currentPageIndex])

  useEffect(() => {
    if (currentPageIndex !== 0) setCurrentPageIndex(0)
    else getTag()
  }, [searchText, pageSize, sort])

  const handleSort = useCallback(
    column => {
      switch (column) {
        case t('tag', 'Tag'):
          setSort(prev =>
            prev === SortEnum.DESC_NAME
              ? ''
              : prev === SortEnum.ASC_NAME
              ? SortEnum.DESC_NAME
              : SortEnum.ASC_NAME,
          )
          break
        case t('last_used', 'Last used'):
          setSort(prev =>
            prev === SortEnum.DESC_UPDATED
              ? ''
              : prev === SortEnum.ASC_UPDATED
              ? SortEnum.DESC_UPDATED
              : SortEnum.ASC_UPDATED,
          )
          break
        case t('created_on', 'Created on'):
          setSort(prev =>
            prev === SortEnum.DESC_CREATED
              ? ''
              : prev === SortEnum.ASC_CREATED
              ? SortEnum.DESC_CREATED
              : SortEnum.ASC_CREATED,
          )
          break
      }
    },
    [setSort],
  )

  const checkSortState = useCallback(
    (column, opertation, sort) => {
      switch (column) {
        case t('tag', 'Tag'):
          return sort === SortEnum.DESC_NAME &&
            opertation === SortOperation.DESC
            ? true
            : sort === SortEnum.ASC_NAME && opertation === SortOperation.ASC
            ? true
            : false

        case t('last_used', 'Last used'):
          return sort === SortEnum.DESC_UPDATED &&
            opertation === SortOperation.DESC
            ? true
            : sort === SortEnum.ASC_UPDATED && opertation === SortOperation.ASC
            ? true
            : false

        case t('created_on', 'Created on'):
          return sort === SortEnum.DESC_CREATED &&
            opertation === SortOperation.DESC
            ? true
            : sort === SortEnum.ASC_CREATED && opertation === SortOperation.ASC
            ? true
            : false
      }
    },
    [setSort],
  )

  const pageOptions = useMemo(() => {
    const pages: number[] = []
    if (pageSize && tagsCount) {
      for (let x = 0; x < ceil(tagsCount / pageSize); x++) pages.push(x)
    }
    return pages
  }, [pageSize, tagsCount])

  const generateDeleteTagLabel = useCallback(() => {
    if (deleteFlag === DeleteFlagEnum.SINGLE_DELETE) return tagData?.label
    else {
      let msg = selectedTags
        .filter((_, index) => index < 3)
        .reduce((info, data) => `${info}, ${data.label}`, '')
        .slice(1)
      if (selectedTags.length > 3)
        msg = `${msg} and ${selectedTags.length - 3} more selected tags`
      return msg
    }
  }, [deleteFlag, DeleteFlagEnum, selectedTags])

  return (
    <AccordionItem>
      <AccordionButton px={12} py={8}>
        <Flex
          flex="1"
          textAlign="left"
          lineHeight="1.5em"
          fontWeight="700"
          style={{ gap: '1em' }}
        >
          <GrTag fontSize="1.5em" />
          {t('suggested_tags', 'Suggested Tags')}
        </Flex>
        <Box background="appPaleGreyThree" borderRadius="50%" padding="0.25em">
          <AccordionIcon color="appSlate" display="block" />
        </Box>
      </AccordionButton>

      <AccordionPanel px={12}>
        <>
          <VStack
            alignItems="start"
            marginTop="1rem"
            width="full"
            spacing={0}
          >
            <Box>
              <ChakraText color={colors.appBattleshipGrey}>
                {t(
                  'suggested_tag_disclaimer',
                  'This list contains tags that you have used in the media library and serve as suggestions.',
                )}
              </ChakraText>
            </Box>

            <Flex align="center" width={'100%'}>
              <SearchBar
                searchQuery={searchText}
                setSearchQuery={setSearchText}
                padding="1.5em 0em"
                minWidth="30em"
              />
              <Spacer />
              {!selectedTags.length && rows.length ? (
                <ChakraText fontWeight="500">
                  {t('showing', 'Showing')}{' '}
                  {currentPageIndex * pageSize + (pageCount === 0 ? 0 : 1)}{' '}
                  {t('to', 'to')}{' '}
                  {currentPageIndex * pageSize + pageSize <= rows.length
                    ? currentPageIndex * pageSize + pageSize
                    : rows.length + currentPageIndex * pageSize}{' '}
                  {t('of', 'of')} {tagsCount} {t('entries', 'entries')}
                </ChakraText>
              ) : null}
              {selectedTags.length !== 0 && (
                <>
                  <Text
                    color="appGreenishTeal"
                    fontWeight="medium"
                  >{`${selectedTags.length} items selected`}</Text>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<BsThreeDots />}
                      variant="link"
                      boxShadow="none"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<RiDeleteBinFill />}
                        color={colors.appBrick}
                        onClick={() => {
                          setDeleteFlag(DeleteFlagEnum.MULTIPLE_DELETE)
                          onOpenDeleteUser()
                        }}
                      >
                        {t('delete', 'Delete')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </>
              )}
            </Flex>
            <Table {...getTableProps}>
              <Thead bgColor="appPaleGreyTwo">
                {headerGroups.map(headerGroup => {
                  const { key, ...restHeaderGroupProps } =
                    headerGroup.getHeaderGroupProps()
                  return (
                    <Tr key={key} {...restHeaderGroupProps}>
                      {headerGroup.headers.map(column => {
                        const { key: keyCol, ...restColumn } =
                          column.getHeaderProps(column.getSortByToggleProps())
                        return (
                          <Th
                            key={keyCol}
                            {...restColumn}
                            onClick={() => handleSort(column?.Header)}
                          >
                            {column.render('Header')}
                            {!column.disableSortBy ? (
                              <Box
                                as="span"
                                pl="2"
                                color={colors.appBattleshipGrey}
                              >
                                {checkSortState(
                                  column.Header,
                                  SortOperation.DESC,
                                  sort,
                                ) ? (
                                  <Icon
                                    as={FaSortUp}
                                    aria-label={t(
                                      'sorted_descending',
                                      'sorted descending',
                                    )}
                                  />
                                ) : checkSortState(
                                    column.Header,
                                    SortOperation.ASC,
                                    sort,
                                  ) ? (
                                  <Icon
                                    as={FaSortDown}
                                    aria-label={t(
                                      'sorted_ascending',
                                      'sorted ascending',
                                    )}
                                  />
                                ) : (
                                  <Icon
                                    as={FaSort}
                                    aria-label={t(
                                      'sort_by_column',
                                      'sort by column',
                                    )}
                                  />
                                )}
                              </Box>
                            ) : null}
                          </Th>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {!loading ? (
                  rows.length ? (
                    page.map(row => {
                      prepareRow(row)
                      const { key, ...restRowProps } = row.getRowProps()
                      return (
                        <Tr key={key} {...restRowProps}>
                          {row.cells.map(cell => {
                            const { key: keyRow, ...restCellProps } =
                              cell.getCellProps()
                            return (
                              <Td key={keyRow} {...restCellProps}>
                                <ChakraText maxW="200px">
                                  {cell.render('Cell')}
                                </ChakraText>
                              </Td>
                            )
                          })}
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={4} textAlign="center">
                        <ChakraText>
                          {t(
                            'no_client_tags',
                            'No tags were added to any file.',
                          )}
                        </ChakraText>
                      </Td>
                    </Tr>
                  )
                ) : (
                  <Tr>
                    <Td colSpan={4} textAlign="center">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                        size="xl"
                      />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
            {pageOptions.length > 0 ? (
              <Divider margin="3em 0em 1em !important" />
            ) : null}
            <Flex width={'100%'}>
              <Spacer />
              <Pagination
                pageIndex={currentPageIndex}
                pageSize={pageSize}
                canPreviousPage={currentPageIndex !== 0}
                canNextPage={
                  tagsCount && pageSize
                    ? currentPageIndex + 1 < ceil(tagsCount / pageSize)
                    : canNextPage
                }
                pageCount={
                  tagsCount && pageSize ? ceil(tagsCount / pageSize) : pageSize
                }
                pageOptions={pageOptions}
                // setPageSize={(size)=>setPageSize(size)}
                setPageSize={setPageSize}
                previousPage={() =>
                  setCurrentPageIndex(prev => (prev !== 0 ? prev - 1 : prev))
                }
                nextPage={() =>
                  setCurrentPageIndex(prev =>
                    prev + 1 < ceil(tagsCount / pageSize) ? prev + 1 : prev,
                  )
                }
                gotoPage={page => setCurrentPageIndex(page)}
              />
            </Flex>
            <DeleteDialog
              isOpen={isOpenDeleteTag}
              isDeleting={isDeleting}
              tagName={generateDeleteTagLabel}
              closeDeleteDialog={() => {
                onCloseDeleteTag()
              }}
              deleteClientTag={deleteTag}
            />
          </VStack>
        </>
      </AccordionPanel>
    </AccordionItem>
  )
}

export default ClientTagsSettings
